/**
 * Documentos financieros bajo demanda (front C-NEW-02 / SP-210, back SP-211).
 *
 * El back reusa la infraestructura de documentos del expediente: subir y
 * revisar son los endpoints genéricos que ya existían, así que aquí solo se
 * agregan los específicos del flujo (solicitar, consultar estado y bloqueo).
 *
 * Los endpoints internos usan `access_token` (staff) vía apiRequest; el del
 * portal usa `supplier_access_token`, de ahí que ese vaya con fetch aparte.
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import type { ColorDeBadge } from '@/shared/components/Badge';
import { config } from '@/config/env';

// El enum del back permite `observed` además de los tres esperados; es poco
// probable en este flujo pero se contempla para no caer en un estado sin mapeo.
export type FinancialDocApiStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'observed';

export interface FinancialDocumentType {
  id: string;
  code: string;
  name: string;
  /**
   * Meses de vigencia configurados para el tipo. Hoy los 4 financieros vienen
   * sin vigencia, así que la fecha es opcional; si algún día se les configura,
   * el portal la pedirá obligatoria sin tocar código.
   */
  validityMonths?: number | null;
  /** Tope de tamaño que valida el back. Se usa para avisar antes de subir. */
  maxFileSizeMb?: number;
}

export interface FinancialLatestDocument {
  id: string;
  version: number;
  fileName: string;
  fileUrl: string;
  status: FinancialDocApiStatus;
  uploadedAt: string;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  /** La captura el proveedor al subir; Contraloría puede corregirla al revisar. */
  validUntil?: string | null;
}

export interface FinancialDocRequirement {
  documentType: FinancialDocumentType;
  latestDocument: FinancialLatestDocument | null;
}

export interface FinancialDocsState {
  /** null = Contraloría todavía no ha solicitado los documentos. */
  requestedAt: string | null;
  /** Solo viene en la respuesta interna (Contraloría), no en la del proveedor. */
  bloqueoDocsFinancieros?: boolean;
  documents: FinancialDocRequirement[];
}

// ── Estados de la UI ─────────────────────────────────────────────────────────

export type FinancialDocEstado = 'pendiente' | 'subido' | 'aprobado' | 'rechazado';

export const estadoDocumento = (
  doc: FinancialLatestDocument | null
): FinancialDocEstado => {
  if (!doc) return 'pendiente';
  switch (doc.status) {
    case 'approved':
      return 'aprobado';
    case 'pending_review':
      return 'subido';
    // rejected y observed se tratan igual: hay que volver a subirlo.
    default:
      return 'rechazado';
  }
};

export const ESTADO_META: Record<
  FinancialDocEstado,
  { label: string; color: ColorDeBadge }
> = {
  pendiente: { label: 'Pendiente', color: 'amarillo' },
  subido: { label: 'Subido', color: 'azul' },
  aprobado: { label: 'Aprobado', color: 'verde' },
  rechazado: { label: 'Rechazado', color: 'rojo' },
};

// ── Fechas ───────────────────────────────────────────────────────────────────
// El back guarda `validUntil` en una columna `date`, así que llega como
// YYYY-MM-DD o como ISO con la hora en cero. En los dos casos la fecha buena es
// la parte inicial: partirla a mano evita que `new Date` la recorra un día por
// zona horaria.

const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}/;

/** Para mostrar: DD/MM/AAAA, o guion si no hay fecha. */
export const fmtFecha = (iso?: string | null): string => {
  if (!iso) return '—';
  const m = SOLO_FECHA.exec(iso);
  if (m) return m[0].split('-').reverse().join('/');
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX');
};

/** Para un `<input type="date">`, que solo entiende YYYY-MM-DD. */
export const aFechaInput = (iso?: string | null): string => {
  if (!iso) return '';
  const m = SOLO_FECHA.exec(iso);
  return m ? m[0] : '';
};

// El back manda `message` como arreglo en los 422 de validación de NestJS.
export const errorMessage = (err: any, fallback: string): string => {
  const m = err?.message;
  if (Array.isArray(m)) return m.filter(Boolean).join(' ');
  return typeof m === 'string' && m.trim() ? m : fallback;
};

// ── Lado interno (Compras / Contraloría) ─────────────────────────────────────

const internalBase = (supplierId: string) =>
  `${API_URL}/internal/suppliers/${supplierId}`;

/** Marca la solicitud como activa y notifica al proveedor. Sin body. */
export const requestFinancialDocuments = (supplierId: string) =>
  apiRequest<{ financialDocsRequestedAt: string }>(
    `${internalBase(supplierId)}/financial-documents/request`,
    { method: 'POST' }
  );

export const getFinancialDocuments = (supplierId: string) =>
  apiRequest<FinancialDocsState>(
    `${internalBase(supplierId)}/financial-documents`
  );

export const setBloqueoDocsFinancieros = (supplierId: string, activo: boolean) =>
  apiRequest<{ bloqueoDocsFinancieros: boolean }>(
    `${internalBase(supplierId)}/bloqueo-docs-financieros`,
    { method: 'PATCH', body: JSON.stringify({ activo }) }
  );

/**
 * Aprobar / rechazar. Es el endpoint genérico de revisión del expediente, el
 * mismo que ya usa el detalle del proveedor, y dispara la notificación solo.
 * `reviewComment` es obligatorio al rechazar.
 *
 * Sobre `validUntil`, que el back distingue en tres casos:
 *   - no mandarlo  → la fecha existente no se toca
 *   - una fecha    → la reemplaza
 *   - null         → la borra
 * Por eso el parámetro es opcional y se manda solo si viene definido: pasar
 * `null` a propósito es la forma de limpiarla.
 *
 * Ojo con `reviewComment`: el back lo asigna siempre, así que al recorregir un
 * documento ya rechazado hay que volver a mandar su comentario o se pierde.
 */
export const reviewFinancialDocument = (
  supplierId: string,
  documentId: string,
  status: 'approved' | 'rejected',
  reviewComment?: string,
  validUntil?: string | null
) =>
  apiRequest<unknown>(
    `${internalBase(supplierId)}/documents/${documentId}/review`,
    {
      method: 'POST',
      body: JSON.stringify({
        status,
        ...(reviewComment ? { reviewComment } : {}),
        ...(validUntil !== undefined ? { validUntil } : {}),
      }),
    }
  );

// ── Portal del proveedor ─────────────────────────────────────────────────────

export const getMisDocumentosFinancieros = async (): Promise<FinancialDocsState> => {
  const token = localStorage.getItem('supplier_access_token');
  if (!token) throw new Error('No se encontró token de autenticación');

  const response = await fetch(
    `${config.apiBaseUrl}/suppliers/profile/financial-documents`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw await response
      .json()
      .catch(() => ({ message: 'No se pudieron cargar los documentos financieros.' }));
  }

  return response.json();
};
